import moment from 'moment'
import { formatCurrency } from 'lib/formatCurrency'

/**
 * Ledger amounts always carry cents. Rounding to whole dollars makes a day's
 * rows fail to add up to its own total: -14.33, -7.95 and -27.34 read as 14, 8
 * and 27 against a net of 50.
 */
export function formatLedgerAmount(value: number): string {
  return formatCurrency(value, 2)
}

/**
 * The CDT codes a practice posts when a booking did not happen, and the word
 * the practice used. Hamblin has 11,353 of them.
 *
 * They are not treatment. They land in the ledger as zero-fee procedures whose
 * only category is "Adjunctive General Services", which is true of the code
 * range and says nothing about what happened.
 *
 * Both words are kept because the practice kept them. Trackable calls the whole
 * class Broken, but missed and cancelled are different events: one is a patient
 * who did not arrive, the other a patient who called ahead. Flattening them
 * would throw away a distinction the practice bothered to record.
 */
const BROKEN_APPOINTMENT_CODES: Record<string, string> = {
  D9986: `Missed appointment`,
  D9987: `Cancelled appointment`,
}

/**
 * What the row is, in one phrase, or undefined when the code is ordinary
 * treatment.
 */
export function brokenAppointmentLabel(code: string | null | undefined): string | undefined {
  return code ? BROKEN_APPOINTMENT_CODES[code.toUpperCase()] : undefined
}

export type TransactionCardInput = {
  amount?: number | null
  transactionSrNo?: string | null
  transactionDate?: string | null
  transactionEntryDate?: string | null
}

// One line of card content: either a money statement (bold headline + muted
// qualifier) or a non-money ledger event rendered as a single muted line.
export type CardLine = { kind: `amount`; headline: string; qualifier?: string } | { kind: `event`; text: string }

/**
 * Kolla encodes the source ledger table in the transactionSrNo prefix:
 * ps- patient payment (paysplit), cpp- insurance claim payment, cpw- insurance
 * claim write-off, adj- manual adjustment, proc- procedure. Other vendors use
 * different formats; consumers must treat unknown prefixes as "no source info".
 */
export function ledgerSource(transactionSrNo: string | null | undefined): string | undefined {
  return /^([a-z]+)-/i.exec(transactionSrNo ?? ``)?.[1]?.toLowerCase()
}

/**
 * The feed anchors cards on transactionDate; the ledger posting date is only
 * worth surfacing when it lands on a different day.
 */
export function postedDateLabel(input: TransactionCardInput): string | undefined {
  const { transactionDate, transactionEntryDate } = input
  if (!transactionDate || !transactionEntryDate) return undefined
  if (String(transactionEntryDate).slice(0, 10) === String(transactionDate).slice(0, 10)) return undefined
  return moment.utc(transactionEntryDate).format(`MMM D, YYYY`)
}

/**
 * Payments are stored ledger-signed (negative = money received), so the
 * headline flips the sign. Positive payments are rare reversals whose exact
 * meaning the PMS does not send - shown signed, without asserting a verb.
 * A missing amount is preserved as missing: only an explicit numeric zero on a
 * cpp row may claim "no payment".
 */
export function paymentLine(input: TransactionCardInput): CardLine {
  const source = ledgerSource(input.transactionSrNo)
  const amount = input.amount
  if (amount == null) {
    return { kind: `event`, text: source === `cpp` ? `Insurance claim processed` : `Amount not recorded` }
  }
  if (amount === 0) {
    if (source === `cpp`) return { kind: `event`, text: `Insurance claim processed · no payment` }
    return { kind: `amount`, headline: formatLedgerAmount(0) }
  }
  if (amount > 0) return { kind: `amount`, headline: `+${formatLedgerAmount(amount)}` }
  const qualifier = source === `cpp` ? `received · insurance` : source === `ps` ? `received · patient` : `received`
  return { kind: `amount`, headline: formatLedgerAmount(Math.abs(amount)), qualifier }
}

/**
 * Adjustments: negative reduces the balance (write-off), positive adds to it.
 * Zero-amount cpw rows are the PMS recording an insurance claim processed with
 * nothing written off. A missing amount is preserved as missing - it never
 * claims "no write-off"/"no balance change".
 */
export function adjustmentLine(input: TransactionCardInput): CardLine {
  const source = ledgerSource(input.transactionSrNo)
  const amount = input.amount
  if (amount == null) {
    return { kind: `event`, text: source === `cpw` ? `Insurance claim processed` : `Amount not recorded` }
  }
  if (amount === 0) {
    if (source === `cpw`) return { kind: `event`, text: `Insurance claim processed · no write-off` }
    return { kind: `event`, text: `No balance change` }
  }
  if (amount > 0) return { kind: `amount`, headline: formatLedgerAmount(amount), qualifier: `added` }
  return {
    kind: `amount`,
    headline: formatLedgerAmount(Math.abs(amount)),
    qualifier: source === `cpw` ? `insurance write-off` : `write-off`,
  }
}

/**
 * Procedures are billed work, stored positive. There is no verb to add: the
 * amount is what the practice charged, and the procedure code beside it says
 * what for.
 */
export function procedureLine(input: TransactionCardInput): CardLine {
  if (input.amount == null) return { kind: `event`, text: `Amount not recorded` }
  return { kind: `amount`, headline: formatLedgerAmount(input.amount) }
}

/**
 * Payments and adjustments reuse the procedureCode column for free text such as
 * "Membership Plan", so only the remaining (procedure) rows have a CDT category.
 */
export function carriesProcedureCategories(type: string | null | undefined): boolean {
  const key = type?.toLowerCase()
  return key !== `payment` && key !== `adjustment`
}

/**
 * The content line for a transaction of any type, dispatched the way the
 * individual cards dispatch it. Collapsed rows and standalone cards then say
 * the same thing about the same record, which is the whole point of grouping
 * them rather than summarising them separately.
 */
export function transactionLine(input: TransactionCardInput & { transactionType?: string | null }): CardLine {
  switch (input.transactionType?.toLowerCase()) {
    case `payment`:
      return paymentLine(input)
    case `adjustment`:
      return adjustmentLine(input)
    default:
      return procedureLine(input)
  }
}

/**
 * Whether a run of transactions is the practice moving money it already had,
 * rather than money arriving or leaving.
 *
 * OpenDental records an income transfer as paired paysplits: one negative
 * taking the allocation off where it sat, one positive putting it where it now
 * belongs, same day, consecutive ids. Louisa Burk has seven such pairs on
 * 2026-07-16, which render as "14 payments" totalling $0.00 and read as though
 * fourteen payments were worthless.
 *
 * Detected by the arithmetic rather than by the ids: every amount must be
 * matched by an equal and opposite one. A day that merely happens to net zero
 * without pairing off is not a transfer and is left alone.
 */
export function isLedgerReallocation(inputs: TransactionCardInput[]): boolean {
  if (inputs.length < 2) return false
  const amounts = inputs.map((input) => input.amount)
  if (amounts.some((amount) => amount == null || amount === 0)) return false

  const remaining = [...(amounts as number[])]
  for (const amount of amounts as number[]) {
    if (amount < 0) continue
    const match = remaining.indexOf(-amount)
    if (match === -1) return false
    remaining.splice(match, 1)
    remaining.splice(remaining.indexOf(amount), 1)
  }
  return remaining.length === 0
}

export type LedgerDaySummary = {
  count: number
  /** Ledger-signed net, or undefined when any row's amount is missing. */
  net?: number
  /** True when at least one row had no amount, so no net can be claimed. */
  incomplete: boolean
}

/**
 * Summarises the transactions posted on one day.
 *
 * Amounts are already ledger-signed, so the net is a plain sum. The one rule
 * that matters: a missing amount is missing, never zero. A day containing one
 * cannot state a net at all, because the number would silently understate what
 * actually moved.
 */
export function ledgerDaySummary(inputs: TransactionCardInput[]): LedgerDaySummary {
  const incomplete = inputs.some((input) => input.amount == null)
  if (incomplete) return { count: inputs.length, incomplete: true }
  return {
    count: inputs.length,
    net: inputs.reduce((total, input) => total + (input.amount ?? 0), 0),
    incomplete: false,
  }
}

/**
 * "$144.00 received" / "$60.00 billed", from a day's ledger-signed net.
 *
 * A day mixes transaction types, so this can only speak in balance terms: money
 * came in, or the balance went up. Where the rows share a type, prefer
 * `ledgerNetLabelForType`, which can name what actually happened.
 */
export function ledgerNetLabel(summary: LedgerDaySummary): string | undefined {
  if (summary.incomplete || summary.net == null) return undefined
  if (summary.net === 0) return formatLedgerAmount(0)
  return summary.net < 0
    ? `${formatLedgerAmount(Math.abs(summary.net))} received`
    : `${formatLedgerAmount(summary.net)} billed`
}

/**
 * The same net, worded for a run that is all one transaction type.
 *
 * Summing three write-offs and calling the result "received" is simply untrue:
 * nobody paid anything. This borrows the vocabulary the individual cards
 * already use, so a collapsed run says what its rows say. Types beyond the
 * three the PMS sends fall back to the balance wording.
 */
export function ledgerNetLabelForType(
  summary: LedgerDaySummary,
  transactionType: string | null | undefined,
): string | undefined {
  if (summary.incomplete || summary.net == null) return undefined
  if (summary.net === 0) return formatLedgerAmount(0)

  const amount = formatLedgerAmount(Math.abs(summary.net))
  switch (transactionType?.toLowerCase()) {
    case `payment`:
      // Positive is a reversal, whose meaning the PMS does not send. Shown
      // signed rather than given a verb we would be inventing.
      return summary.net < 0 ? `${amount} received` : `+${amount}`
    case `adjustment`:
      return summary.net < 0 ? `${amount} written off` : `${amount} added`
    default:
      return ledgerNetLabel(summary)
  }
}
